import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  People,
  Web,
  Receipt,
  Email,
  TrendingUp,
  AttachMoney,
  BusinessCenter,
  ContactMail,
  Analytics,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { crmService, CRMAnalyticsData } from '../services/crmService';

interface DashboardStats {
  clients: {
    total: number;
    thisMonth: number;
    growth: number;
  };
  sites: {
    total: number;
    live: number;
    staged: number;
  };
  invoices: {
    total: number;
    pending: number;
    overdue: number;
    totalAmount: number;
  };
  emails: {
    sentToday: number;
    sentThisMonth: number;
    deliveryRate: number;
  };
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [crmAnalytics, setCrmAnalytics] = useState<CRMAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('redex_admin_token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch('/api/dashboard/stats', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.data) {
          setStats(data.data);
        }

        try {
          const crmData = await crmService.getAnalytics('month');
          setCrmAnalytics(crmData);
        } catch (crmError) {
          console.warn('CRM analytics unavailable:', crmError);
        }

      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [navigate]);

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    subtitle?: string;
  }> = ({ title, value, icon, subtitle }) => (
    <Card 
      sx={{ 
        height: '100%',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
          borderColor: 'primary.main',
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.75rem'
            }}
          >
            {title}
          </Typography>
          <Box sx={{ color: 'primary.main', opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
        <Typography 
          variant="h3" 
          component="div" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 700,
            mb: subtitle ? 0.5 : 0
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const QuickAction: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
  }> = ({ title, description, icon, onClick }) => (
    <Card
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
          borderColor: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Box 
            sx={{ 
              color: 'primary.main',
              fontSize: 40,
              opacity: 0.9
            }}
          >
            {icon}
          </Box>
          <Box flex={1}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'text.primary',
                fontWeight: 600,
                mb: 0.5
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: 'text.secondary' }}
            >
              {description}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
        sx={{ bgcolor: 'background.default' }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, bgcolor: 'background.default', minHeight: '80vh' }}>
        <Alert 
          severity="error"
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: 'text.primary',
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        <Box mb={4}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ color: 'text.primary', fontWeight: 700, mb: 1, letterSpacing: -0.5 }}
          >
            Dashboard
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ color: 'text.secondary', fontWeight: 400 }}
          >
            Welcome to REDEX Admin - Lead Generation & Client Management Platform
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Clients"
              value={stats?.clients?.total || 0}
              icon={<People fontSize="large" />}
              subtitle={`+${stats?.clients?.thisMonth || 0} this month`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Live Sites"
              value={stats?.sites?.live || 0}
              icon={<Web fontSize="large" />}
              subtitle={`${stats?.sites?.staged || 0} staged`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={`$${(stats?.invoices?.totalAmount || 0).toLocaleString()}`}
              icon={<AttachMoney fontSize="large" />}
              subtitle={`${stats?.invoices?.pending || 0} pending invoices`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Client Growth"
              value={`+${(stats?.clients?.growth || 0)}%`}
              icon={<TrendingUp fontSize="large" />}
              subtitle="vs last month"
            />
          </Grid>
        </Grid>

        {crmAnalytics && crmAnalytics.leads && crmAnalytics.revenue && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography 
                variant="h5" 
                sx={{ color: 'primary.main', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                CRM Performance
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Leads"
                value={crmAnalytics.leads.total || 0}
                icon={<BusinessCenter fontSize="large" />}
                subtitle={`${crmAnalytics.leads.qualified || 0} qualified`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Conversion Rate"
                value={`${((crmAnalytics.conversionRate || 0) * 100).toFixed(1)}%`}
                icon={<Analytics fontSize="large" />}
                subtitle={`${crmAnalytics.totalLeads || 0} total leads`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Contacts"
                value={crmAnalytics.totalLeads || 0}
                icon={<ContactMail fontSize="large" />}
                subtitle={`${crmAnalytics.qualifiedLeads || 0} qualified`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="CRM Revenue"
                value={`$${(crmAnalytics.revenue.total || 0).toLocaleString()}`}
                icon={<AttachMoney fontSize="large" />}
                subtitle={`$${(crmAnalytics.revenueGenerated || 0).toLocaleString()} generated`}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography 
              variant="h5" 
              sx={{ color: 'text.primary', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
            >
              Quick Actions
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Create Client"
              description="Add a new client to the system"
              icon={<Add />}
              onClick={() => navigate('/clients')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Generate Invoice"
              description="Create and send a new invoice"
              icon={<Receipt />}
              onClick={() => navigate('/invoices')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Send Email Campaign"
              description="Create marketing email sequence"
              icon={<Email />}
              onClick={() => navigate('/emails')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Generate Leads"
              description="Use AI to find new business leads"
              icon={<BusinessCenter />}
              onClick={() => navigate('/leads')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Manage Pipeline"
              description="Track subscriptions and deliveries"
              icon={<Analytics />}
              onClick={() => navigate('/pipeline')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickAction
              title="Add Contact"
              description="Add new contact to CRM database"
              icon={<ContactMail />}
              onClick={() => navigate('/contacts')}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Paper sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ color: 'text.primary', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                System Status
              </Typography>
              <List>
                <ListItem sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <ListItemText
                    primary={<Typography sx={{ color: 'text.primary', fontWeight: 500 }}>✅ All Systems Operational</Typography>}
                    secondary={<Typography sx={{ color: 'text.secondary' }}>Dashboard, CRM, Pipeline, Email sequences all running</Typography>}
                  />
                </ListItem>
                <ListItem sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <ListItemText
                    primary={<Typography sx={{ color: 'text.primary', fontWeight: 500 }}>✅ Database Connected</Typography>}
                    secondary={<Typography sx={{ color: 'text.secondary' }}>PostgreSQL database healthy and responsive</Typography>}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={<Typography sx={{ color: 'text.primary', fontWeight: 500 }}>🚀 Ready for Lead Generation</Typography>}
                    secondary={<Typography sx={{ color: 'primary.main' }}>Navigate to Leads page to begin generating business opportunities</Typography>}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardPage;
